/**
 * ErrorHandler.ts
 *
 * Centralized error handling for simulation services
 */

export enum ErrorType {
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  SIMULATION_ERROR = 'SIMULATION_ERROR',
  MONTE_CARLO_ERROR = 'MONTE_CARLO_ERROR',
  DATA_ERROR = 'DATA_ERROR',
  CALCULATION_ERROR = 'CALCULATION_ERROR',
}

export interface SimulationError {
  type: ErrorType;
  message: string;
  details?: string;
  timestamp: Date;
}

export class ErrorHandler {
  /**
   * Handle and format simulation errors
   */
  static handleError(error: unknown, type: ErrorType, context?: string): SimulationError {
    const timestamp = new Date();
    let message = 'An unexpected error occurred';
    let details = '';

    if (error instanceof Error) {
      message = error.message;
      details = error.stack || '';
    } else if (typeof error === 'string') {
      message = error;
    } else {
      details = JSON.stringify(error);
    }

    // Add context if provided
    if (context) {
      message = `${context}: ${message}`;
    }

    // Log error for debugging
    console.error(`[${type}] ${message}`, {
      details,
      timestamp,
      context,
    });

    return {
      type,
      message,
      details,
      timestamp,
    };
  }

  /**
   * Validate simulation parameters
   */
  static validateParameters(steuerParameter: any): SimulationError | null {
    try {
      if (!steuerParameter) {
        return this.handleError('Steuerparameter sind erforderlich', ErrorType.VALIDATION_ERROR);
      }

      // Check required fields
      const requiredFields = [
        'anzahlJahreTopf2',
        'retirementAge',
        'berechnungBisLebensalter',
        'zinssatzVerbrauchstopf',
        'zinssatzZinstopf',
        'renditeInvestitionstopf',
        'inflationsrate',
        'vorhandenesKapital',
        'portfolioEntnahmeRate',
      ];

      for (const field of requiredFields) {
        if (steuerParameter[field] === undefined || steuerParameter[field] === null) {
          return this.handleError(`Pflichtfeld fehlt: ${field}`, ErrorType.VALIDATION_ERROR);
        }
      }

      // Validate vorhandenesKapital: muss endlich und > 0 sein
      if (!isFinite(steuerParameter.vorhandenesKapital)) {
        return this.handleError(
          'Vorhandenes Kapital muss eine endliche Zahl sein',
          ErrorType.VALIDATION_ERROR
        );
      }
      if (steuerParameter.vorhandenesKapital <= 0) {
        return this.handleError(
          'Vorhandenes Kapital muss größer als 0 sein',
          ErrorType.VALIDATION_ERROR
        );
      }

      // Validate portfolioEntnahmeRate: endlich, >= 0 und <= 100
      if (!isFinite(steuerParameter.portfolioEntnahmeRate)) {
        return this.handleError(
          'Portfolio-Entnahmerate muss eine endliche Zahl sein',
          ErrorType.VALIDATION_ERROR
        );
      }
      if (steuerParameter.portfolioEntnahmeRate < 0 || steuerParameter.portfolioEntnahmeRate > 100) {
        return this.handleError(
          'Portfolio-Entnahmerate muss zwischen 0% und 100% liegen',
          ErrorType.VALIDATION_ERROR
        );
      }

      // Validate numeric ranges
      // Anzahl Jahre Topf 2: 0 ist erlaubt (legitime "kein Zinstopf"-Konfiguration)
      if (steuerParameter.anzahlJahreTopf2 < 0 || !isFinite(steuerParameter.anzahlJahreTopf2) || !Number.isInteger(steuerParameter.anzahlJahreTopf2)) {
        return this.handleError(
          'Anzahl Jahre Topf 2 muss eine nicht-negative ganze Zahl sein',
          ErrorType.VALIDATION_ERROR
        );
      }

      if (steuerParameter.retirementAge <= 0 || steuerParameter.retirementAge > 100) {
        return this.handleError(
          'Alter bei Rentenbeginn muss zwischen 1 und 100 Jahren liegen',
          ErrorType.VALIDATION_ERROR
        );
      }

      if (steuerParameter.berechnungBisLebensalter <= steuerParameter.retirementAge) {
        return this.handleError(
          'Berechnung bis Lebensalter muss größer als Alter bei Rentenbeginn sein',
          ErrorType.VALIDATION_ERROR
        );
      }

      if (
        steuerParameter.zinssatzVerbrauchstopf < 0 ||
        steuerParameter.zinssatzVerbrauchstopf > 100
      ) {
        return this.handleError(
          'Zinssatz Verbrauchstopf muss zwischen 0% und 100% liegen',
          ErrorType.VALIDATION_ERROR
        );
      }

      if (steuerParameter.zinssatzZinstopf < 0 || steuerParameter.zinssatzZinstopf > 100) {
        return this.handleError(
          'Zinssatz Zinstopf muss zwischen 0% und 100% liegen',
          ErrorType.VALIDATION_ERROR
        );
      }

      if (
        steuerParameter.renditeInvestitionstopf < -100 ||
        steuerParameter.renditeInvestitionstopf > 100
      ) {
        return this.handleError(
          'Rendite Investitionstopf muss zwischen -100% und 100% liegen',
          ErrorType.VALIDATION_ERROR
        );
      }

      // Inflationsrate: bis -10% erlaubt (negative Parameter-Inflation, z.B. Deflation)
      // nur < -10% ablehnen
      if (steuerParameter.inflationsrate < -10 || steuerParameter.inflationsrate > 50) {
        return this.handleError(
          'Inflationsrate muss zwischen -10% und 50% liegen',
          ErrorType.VALIDATION_ERROR
        );
      }

      return null; // No validation errors
    } catch (error) {
      return this.handleError(error, ErrorType.VALIDATION_ERROR, 'Parameter validation');
    }
  }

  /**
   * Validate simulation options
   */
  static validateSimulationOptions(options: any): SimulationError | null {
    try {
      if (!options) {
        return this.handleError(
          'Simulationsoptionen sind erforderlich',
          ErrorType.VALIDATION_ERROR
        );
      }

      const validInflationOptions = [
        'keine-inflation',
        'parameter-inflation',
        'historische-inflation',
        'statistisch-inflation',
      ];
      if (!validInflationOptions.includes(options.inflationOption)) {
        return this.handleError('Ungültige Inflations-Option', ErrorType.VALIDATION_ERROR);
      }

      const validReturnOptions = [
        'keine-rendite',
        'konstante-rendite',
        'historische-rendite',
        'statistisch-rendite',
        'historische-garch',
        'statistisch-garch',
      ];
      if (!validReturnOptions.includes(options.investmentReturnOption)) {
        return this.handleError(
          `Ungültige Investment-Return-Option: "${options.investmentReturnOption}"`,
          ErrorType.VALIDATION_ERROR
        );
      }

      return null; // No validation errors
    } catch (error) {
      return this.handleError(error, ErrorType.VALIDATION_ERROR, 'Options validation');
    }
  }

  /**
   * Create user-friendly error message
   */
  static getUserFriendlyMessage(error: SimulationError): string {
    switch (error.type) {
      case ErrorType.VALIDATION_ERROR:
        return `Eingabefehler: ${error.message}`;
      case ErrorType.SIMULATION_ERROR:
        return `Simulationsfehler: ${error.message}`;
      case ErrorType.MONTE_CARLO_ERROR:
        return `Monte-Carlo-Fehler: ${error.message}`;
      case ErrorType.DATA_ERROR:
        return `Datenfehler: ${error.message}`;
      case ErrorType.CALCULATION_ERROR:
        return `Berechnungsfehler: ${error.message}`;
      default:
        return `Unbekannter Fehler: ${error.message}`;
    }
  }

  /**
   * Check if error is recoverable
   */
  static isRecoverable(error: SimulationError): boolean {
    switch (error.type) {
      case ErrorType.VALIDATION_ERROR:
        return true; // User can fix input
      case ErrorType.DATA_ERROR:
        return true; // User can retry or change parameters
      case ErrorType.SIMULATION_ERROR:
      case ErrorType.MONTE_CARLO_ERROR:
      case ErrorType.CALCULATION_ERROR:
        return false; // System errors, need investigation
      default:
        return false;
    }
  }
}
